/**
 * Helpers for form field value access and select options.
 */

import {
  INDIVIDUAL_FILING_STATUS,
  MARITAL_STATUS,
  PERSON_TYPE,
  RELATION_WITH_TAXPAYER,
  COVERAGE_SOURCE,
  HEALTH_FORM_TYPE,
  US_STATE_CODES,
  BUSINESS_TYPE,
  TAXPAYER_ID_TYPE,
  ACCOUNTING_METHOD,
  MILEAGE_METHOD,
} from '@/constants/formConstants';

/** Get value at path (e.g. 'contact.emailId', 'expenses.0.amount') */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const p of parts) {
    if (current == null) return undefined;
    const num = Number(p);
    current = typeof num === 'number' && !Number.isNaN(num) && Array.isArray(current)
      ? (current as unknown[])[num]
      : (current as Record<string, unknown>)[p];
  }
  return current;
}

/** Set value at path and return new object (immutable). Supports plain objects and arrays. */
export function setNestedValue(obj: unknown, path: string, value: unknown): unknown {
  if (obj == null && path.includes('.')) {
    const parts = path.split('.');
    const key = parts.pop()!;
    return { [key]: value };
  }
  const parts = path.split('.');
  if (parts.length === 1) {
    if (Array.isArray(obj)) {
      const copy = [...obj];
      const i = Number(parts[0]);
      if (!Number.isNaN(i)) {
        copy[i] = value;
        return copy;
      }
    }
    return { ...(obj as Record<string, unknown>), [parts[0]]: value };
  }
  const [head, ...rest] = parts;
  const restPath = rest.join('.');
  const num = Number(head);
  const isArrayIndex = !Number.isNaN(num) && Array.isArray(obj);
  const current = isArrayIndex ? (obj as unknown[])[num] : (obj as Record<string, unknown>)?.[head];
  const next = setNestedValue(current, restPath, value);
  if (Array.isArray(obj)) {
    const copy = [...obj];
    copy[num] = next;
    return copy;
  }
  return { ...(obj as Record<string, unknown>), [head]: next };
}

/** Title-case a string (e.g. "FOSTER CHILD" -> "Foster Child") */
function toTitleCase(s: string): string {
  return s
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const INDIVIDUAL_OPTIONS: Record<string, { value: string; label: string }[]> = {
  INDIVIDUAL_FILING_STATUS: Object.entries(INDIVIDUAL_FILING_STATUS).map(([k, v]) => ({
    value: v,
    label: toTitleCase(k),
  })),
  PERSON_TYPE: Object.entries(PERSON_TYPE).map(([k, v]) => ({ value: v, label: toTitleCase(k) })),
  MARITAL_STATUS: Object.entries(MARITAL_STATUS).map(([k, v]) => ({ value: v, label: toTitleCase(k) })),
  RELATION_WITH_TAXPAYER: Object.entries(RELATION_WITH_TAXPAYER).map(([k, v]) => ({
    value: v,
    label: k === 'FOSTER_CHILD' ? 'Foster Child' : toTitleCase(k),
  })),
  COVERAGE_SOURCE: Object.entries(COVERAGE_SOURCE).map(([k, v]) => ({ value: v, label: toTitleCase(k) })),
  HEALTH_FORM_TYPE: Object.entries(HEALTH_FORM_TYPE).map(([k, v]) => ({
    value: v,
    label: `${k.replace('FORM_', '')} (${v})`,
  })),
};

const BUSINESS_OPTIONS: Record<string, { value: string; label: string }[]> = {
  BUSINESS_TYPE: Object.entries(BUSINESS_TYPE).map(([k, v]) => ({
    value: v,
    label: toTitleCase(k),
  })),
  TAXPAYER_ID_TYPE: Object.entries(TAXPAYER_ID_TYPE).map(([k, v]) => ({ value: v, label: k })),
  ACCOUNTING_METHOD: Object.entries(ACCOUNTING_METHOD).map(([k, v]) => ({ value: v, label: toTitleCase(k) })),
  MILEAGE_METHOD: Object.entries(MILEAGE_METHOD).map(([k, v]) => ({ value: v, label: toTitleCase(k) })),
};

export function getIndividualSelectOptions(optionsKey: string): { value: string; label: string }[] {
  return INDIVIDUAL_OPTIONS[optionsKey] ?? [];
}

export function getBusinessSelectOptions(optionsKey: string): { value: string; label: string }[] {
  return BUSINESS_OPTIONS[optionsKey] ?? [];
}

export function getStateOptions(): { value: string; label: string }[] {
  return US_STATE_CODES.map((s) => ({ value: s, label: s }));
}
